import React, { useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { BookOpenIcon, DownloadIcon, EyeIcon, FileCheckIcon, FileTextIcon, FolderIcon, LockIcon, PenLineIcon, ReceiptIcon, SearchIcon, SearchXIcon, Trash2Icon, UploadCloudIcon, UploadIcon, UserRoundIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { Modal } from '../../components/ui/Modal';
import { PageHeader } from '../../components/ui/PageHeader';
import { useSelfService } from '../../hooks/useSelfService';
import type { SelfDoc, SelfFolderId } from '../../types/selfService';
import type { IconComponent } from '../../types/ui';
import { TODAY, formatDate } from '../../utils/format';
import { hrActions } from '../../utils/hrStore';
const FOLDERS: {
  id: SelfFolderId;
  name: string;
  icon: IconComponent;
  hint: string;
}[] = [{
  id: 'signed',
  name: 'Signed Agreements',
  icon: FileCheckIcon,
  hint: 'Completed via DocuSign'
}, {
  id: 'offer',
  name: 'Offer Letter',
  icon: FileTextIcon,
  hint: 'Your signed offer'
}, {
  id: 'tax',
  name: 'Tax Forms',
  icon: ReceiptIcon,
  hint: 'W-4 and state forms'
}, {
  id: 'handbook',
  name: 'Policies & Handbook',
  icon: BookOpenIcon,
  hint: 'Company policies'
}, {
  id: 'submitted',
  name: 'Submitted by me',
  icon: UserRoundIcon,
  hint: 'Files you uploaded'
}];
function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
export function MyDocuments() {
  const {
    docs
  } = useSelfService();
  const [folder, setFolder] = useState<SelfFolderId | 'all'>('all');
  const [query, setQuery] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [signing, setSigning] = useState<SelfDoc | null>(null);
  const [deleting, setDeleting] = useState<SelfDoc | null>(null);
  const needsSignature = docs.filter(d => d.needsSignature);
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return docs.filter(d => (folder === 'all' || d.folder === folder) && (!q || d.name.toLowerCase().includes(q)));
  }, [docs, folder, query]);
  return <div data-id="element-1552" data-mp="34auhkpv62isk">
      <PageHeader eyebrow="My documents" title="Documents" description="View and download your documents. Files from HR can't be deleted." actions={<Button variant="primary" icon={UploadIcon} onClick={() => setUploadOpen(true)} data-id="element-1554" data-mp="3e65v54uu9jyq">
            Upload
          </Button>} data-id="element-1553" data-mp="34am3wvr7z9gb" />

      {needsSignature.map(d => <div key={d.id} className="mb-6 flex flex-col gap-4 rounded-2xl bg-flame/[0.07] p-5 ring-1 ring-inset ring-flame/25 sm:flex-row sm:items-center" data-id="element-1555" data-mp="3nw8o551ymxx2">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-flame text-white" data-id="element-1556" data-mp="3rx0yy2qhdavg">
            <PenLineIcon className="h-5 w-5" aria-hidden="true" data-id="element-1557" data-mp="1lad46cfxf5zv" />
          </span>
          <div className="min-w-0 flex-1" data-id="element-1558" data-mp="1l88rb501uwng">
            <p className="text-sm font-semibold text-navy" data-id="element-1559" data-mp="1gb8s9e1mau5u">Needs your signature</p>
            <p className="text-sm text-slate-600" data-id="element-1560" data-mp="1c9ew9j2daec0">
              {d.name} · sent by HR {formatDate(d.date, 'MMM d')} via <span className="font-semibold text-[#1f4fd1]" data-id="element-1561" data-mp="17bltl009w2gh">DocuSign</span>
            </p>
          </div>
          <Button variant="primary" icon={PenLineIcon} onClick={() => setSigning(d)} data-id="element-1562" data-mp="241cq14s4jzox">
            Review & sign
          </Button>
        </div>)}

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5" data-id="element-1563" data-mp="201vg9z5o0gdy">
        {FOLDERS.map(({
        id,
        name,
        icon: Icon,
        hint
      }) => {
        const count = docs.filter(d => d.folder === id).length;
        const active = folder === id;
        return <button key={id} type="button" onClick={() => setFolder(active ? 'all' : id)} aria-pressed={active} className={`flex flex-col rounded-2xl p-4 text-left shadow-soft ring-1 transition hover:-translate-y-0.5 hover:shadow-lift ${active ? 'bg-navy text-white ring-navy' : 'bg-white ring-slate-900/[0.04]'}`} data-id="element-1564" data-mp="2nqn2m9wkq4it">
              <span className="flex items-center justify-between" data-id="element-1565" data-mp="1tkiw5za3edbu">
                <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${active ? 'bg-white/10 text-flame' : id === 'submitted' ? 'bg-flame/10 text-flame' : 'bg-azure/10 text-azure'}`} data-id="element-1566" data-mp="1yhsrfvk5xj7l">
                  <Icon className="h-5 w-5" aria-hidden="true" data-id="element-1567" data-mp="2q7x5zdl8rc0w" />
                </span>
                <span className={`text-xl font-semibold ${active ? 'text-white' : 'text-navy'}`} data-id="element-1568" data-mp="2cg8yxopundqs">{count}</span>
              </span>
              <span className={`mt-4 text-sm font-semibold ${active ? 'text-white' : 'text-navy'}`} data-id="element-1569" data-mp="2he0s0m28d5wk">{name}</span>
              <span className={`text-xs ${active ? 'text-white/60' : 'text-muted'}`} data-id="element-1570" data-mp="39wfqviz7s39s">{hint}</span>
            </button>;
      })}
      </div>

      <section className="mt-6 rounded-2xl bg-white shadow-soft ring-1 ring-slate-900/[0.04]" data-id="element-1571" data-mp="3j9wz5ms7oxwx">
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-1572" data-mp="3f8uxz0eqibsb">
          <p className="px-2 text-sm font-semibold text-navy" data-id="element-1573" data-mp="3t09g750ti8yb">
            {folder === 'all' ? 'All documents' : FOLDERS.find(f => f.id === folder)?.name}
            <span className="ml-2 font-normal text-muted" data-id="element-1574" data-mp="30bltr64c1dj6">{visible.length}</span>
          </p>
          <div className="relative sm:w-72" data-id="element-1575" data-mp="2wbjzvj4u9oeg">
            <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-1576" data-mp="2wclf7zsw0x1z" />
            <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search my documents" aria-label="Search my documents" className="h-10 w-full rounded-xl border-0 bg-canvas pl-10 pr-3 text-sm text-navy ring-1 ring-inset ring-slate-200 placeholder:text-mutedlight focus:bg-white focus:outline-none focus:ring-2 focus:ring-azure" data-id="element-1577" data-mp="35bqcz3wmkmd3" />
          </div>
        </div>
        {visible.length === 0 ? <div className="p-6" data-id="element-1578" data-mp="11nn81jq0ss2s">
            <EmptyState icon={SearchXIcon} title="No documents here" description={folder === 'submitted' ? 'Upload a file and it will appear in this folder.' : 'Try another folder or search.'} data-id="element-1579" data-mp="11kiv9xyhoo0i" />
          </div> : <ul className="divide-y divide-slate-100" data-id="element-1580" data-mp="dx0zzsg0n7ss">
            {visible.map(d => <li key={d.id} className="flex flex-col gap-3 px-6 py-3.5 transition-colors hover:bg-azure/[0.03] sm:flex-row sm:items-center" data-id="element-1581" data-mp="duoh9d2foteo">
                <div className="flex min-w-0 flex-1 items-center gap-3" data-id="element-1582" data-mp="nnvybv6jdxul">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-canvas text-navy" data-id="element-1583" data-mp="3md45brjnokdy">
                    <FileTextIcon className="h-5 w-5" aria-hidden="true" data-id="element-1584" data-mp="3rb5p6eowonqh" />
                  </span>
                  <div className="min-w-0" data-id="element-1585" data-mp="3jsodq2xgyyuv">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-navy" data-id="element-1586" data-mp="3jqbw5qvmiqic">
                      <span className="truncate" data-id="element-1587" data-mp="3er8pk1zzwc89">{d.name}</span>
                      {d.signedVia && <Badge tone="blue" data-id="element-1588" data-mp="39snq44skhwkv">DocuSign</Badge>}
                      {d.needsSignature && <Badge tone="orange" data-id="element-1589" data-mp="34u4464dz4ksi">Awaiting signature</Badge>}
                    </p>
                    <p className="text-xs text-muted" data-id="element-1590" data-mp="30ults2js3s3x">
                      {FOLDERS.find(f => f.id === d.folder)?.name} · {d.uploadedBy === 'Me' ? 'Uploaded by you' : 'From HR'} · {formatDate(d.date, 'MMM d, yyyy')} · {d.size}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 pl-[52px] sm:pl-0" data-id="element-1591" data-mp="2m5pbzdrx70q4">
                  <IconButton label={`View ${d.name}`} icon={EyeIcon} onClick={() => toast(`Opening ${d.name}`, {
              description: 'Preview opens in a new tab.'
            })} data-id="element-1592" data-mp="2h5bbcgsnet24" />
                  <IconButton label={`Download ${d.name}`} icon={DownloadIcon} onClick={() => toast.success('Download started', {
              description: d.name
            })} data-id="element-1593" data-mp="2d62kpkswnbas" />
                  {d.uploadedBy === 'Me' ? <IconButton label={`Delete ${d.name}`} icon={Trash2Icon} danger onClick={() => setDeleting(d)} data-id="element-1594" data-mp="9sa17stfqrkr" /> : <span className="flex h-8 w-8 items-center justify-center text-slate-300" title="HR documents can't be deleted" data-id="element-1595" data-mp="3w11hs7b5mjbp">
                      <LockIcon className="h-4 w-4" aria-label="HR documents can't be deleted" data-id="element-1596" data-mp="3i8led5eccja9" />
                    </span>}
                </div>
              </li>)}
          </ul>}
      </section>

      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={() => setFolder('submitted')} data-id="element-1597" data-mp="38a5o14n51e1t" />

      <Modal open={signing !== null} onClose={() => setSigning(null)} title="Review & sign" description="Demo DocuSign envelope — no real signature is captured." footer={<>
            <Button onClick={() => setSigning(null)} data-id="element-1599" data-mp="2kpmw42y8srdj">Not now</Button>
            <Button variant="primary" icon={PenLineIcon} onClick={() => {
        if (signing) hrActions.signSelfDoc(signing.id);
        toast.success('Signed with DocuSign', {
          description: 'A copy was filed in Signed Agreements.'
        });
        setSigning(null);
      }} data-id="element-1600" data-mp="2fps57zegw1sh">
              Sign document
            </Button>
          </>} data-id="element-1598" data-mp="2zbk352u71mxv">
        <div className="rounded-xl bg-canvas p-4 text-sm text-slate-600" data-id="element-1601" data-mp="2ynl6yydcu5oa">
          <p className="font-semibold text-navy" data-id="element-1602" data-mp="3cg11fas6wvdl">{signing?.name}</p>
          <p className="mt-2 leading-relaxed" data-id="element-1603" data-mp="38e61a9lzwo1e">I acknowledge that I have received, read and understand the Madison Mortgage 2026 Employee Handbook, and agree to follow the policies it describes.</p>
          <p className="mt-4 font-[cursive] text-2xl text-navy" data-id="element-1604" data-mp="3mbmbzxbbr3yy">Jordan Sample</p>
        </div>
      </Modal>

      <Modal open={deleting !== null} onClose={() => setDeleting(null)} title="Delete this file?" description={deleting?.name} footer={<>
            <Button onClick={() => setDeleting(null)} data-id="element-1606" data-mp="clcanary9max">Cancel</Button>
            <Button variant="danger" icon={Trash2Icon} onClick={() => {
        if (deleting) hrActions.deleteSelfDoc(deleting.id);
        toast('File deleted', {
          description: deleting?.name
        });
        setDeleting(null);
      }} data-id="element-1607" data-mp="clkob4vwcvn6">
              Delete
            </Button>
          </>} data-id="element-1605" data-mp="ldmlmuao5vt8">
        <p className="text-sm text-slate-600" data-id="element-1608" data-mp="va93rbbm28rk">You can only delete files you uploaded. HR keeps its own copy of anything already processed.</p>
      </Modal>
    </div>;
}
function IconButton({
  label,
  icon: Icon,
  onClick,
  danger
}: {
  label: string;
  icon: IconComponent;
  onClick: () => void;
  danger?: boolean;
}) {
  return <button type="button" onClick={onClick} aria-label={label} title={label} className={`flex h-8 w-8 items-center justify-center rounded-lg text-muted transition ${danger ? 'hover:bg-rose-50 hover:text-rose-600' : 'hover:bg-canvas hover:text-navy'}`} data-id="element-1609" data-mp="3b5196gb4hj6u">
      <Icon className="h-4 w-4" data-id="element-1610" data-mp="3avwasd0frneh" />
    </button>;
}
function UploadModal({
  open,
  onClose,
  onUploaded
}: {
  open: boolean;
  onClose: () => void;
  onUploaded: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const close = () => {
    setFiles([]);
    onClose();
  };
  const save = () => {
    if (!files.length) return;
    const date = format(TODAY, 'yyyy-MM-dd');
    hrActions.addSelfDocs(files.map((f, i) => ({
      id: `up-${Date.now()}-${i}`,
      name: f.name,
      folder: 'submitted',
      uploadedBy: 'Me',
      date,
      size: formatSize(f.size)
    })));
    toast.success(`${files.length} ${files.length === 1 ? 'file' : 'files'} uploaded`, {
      description: 'Saved to Submitted by me. HR has been notified.'
    });
    onUploaded();
    close();
  };
  return <Modal open={open} onClose={close} title="Upload to Submitted by me" description="HR can see everything in this folder." footer={<>
          <Button onClick={close} data-id="element-1612" data-mp="216mmimvnu1hw">Cancel</Button>
          <Button variant="primary" icon={UploadIcon} onClick={save} disabled={!files.length} data-id="element-1613" data-mp="215upr8qbtxq1">
            Upload {files.length ? `(${files.length})` : ''}
          </Button>
        </>} data-id="element-1611" data-mp="1r9rlnxbwzp0g">
      <button type="button" onClick={() => inputRef.current?.click()} onDragOver={e => {
      e.preventDefault();
      setDragging(true);
    }} onDragLeave={() => setDragging(false)} onDrop={e => {
      e.preventDefault();
      setDragging(false);
      setFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)]);
    }} className={`flex w-full flex-col items-center rounded-2xl border-2 border-dashed px-6 py-10 text-center transition ${dragging ? 'border-azure bg-azure/[0.05]' : 'border-slate-200 hover:border-azure/50'}`} data-id="element-1614" data-mp="2a7ay7axkzprm">
        <UploadCloudIcon className="h-8 w-8 text-azure" aria-hidden="true" data-id="element-1615" data-mp="3hgugm5904u9d" />
        <span className="mt-3 text-sm font-medium text-navy" data-id="element-1616" data-mp="33j5wx0qo7t5w">Drag files here or click to browse</span>
        <span className="mt-1 text-xs text-muted" data-id="element-1617" data-mp="38gpby2v709yw">PDF, JPG or PNG · up to 10 MB each</span>
      </button>
      <input ref={inputRef} type="file" multiple className="sr-only" onChange={e => setFiles(prev => [...prev, ...Array.from(e.target.files ?? [])])} data-id="element-1618" data-mp="2trjdih46ins2" />
      {files.length > 0 && <ul className="mt-4 space-y-2" data-id="element-1619" data-mp="2kuskkdkxbp1p">
          {files.map((f, i) => <li key={`${f.name}-${i}`} className="flex items-center gap-3 rounded-xl bg-canvas px-3 py-2 text-sm" data-id="element-1620" data-mp="2ovkqawul7bt8" data-mpi={i}>
              <FolderIcon className="h-4 w-4 text-azure" aria-hidden="true" data-id="element-1621" data-mp="2ava0zy8xe5a6" />
              <span className="min-w-0 flex-1 truncate text-navy" data-id="element-1622" data-mp="2fstg10dg6m36">{f.name}</span>
              <span className="text-xs text-muted" data-id="element-1623" data-mp="3i7sl97o5k1au">{formatSize(f.size)}</span>
            </li>)}
        </ul>}
    </Modal>;
}